import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  formatJenisPengukuranLine,
  strapiEnsureSamplingScheduleForQuotation,
  strapiGetQuotation,
  type StrapiQuotationGroupRow,
  type StrapiQuotationRow,
  type StrapiSampleInformationRow,
  type StrapiSamplingScheduleRow,
} from '../../api/strapiCrm';
import { buildGroupLabel } from '../SamplingSchedulesPage/samplingSchedulePlans';

type ContractScheduleRow = {
  eventId: string;
  title: string;
  jenisPengukuranLabel: string;
  recurringDateLabel: string;
  scheduleId: string;
  workOrderId: string | null;
  approvedDatesLabel: string | null;
  approvedByAdminSampling: boolean;
};

type Props = {
  open: boolean;
  jwt: string | null;
  quotationDocumentId: string;
  onClose: () => void;
  onEnsured?: () => void;
};

function scheduleApprovedByAdminSampling(schedule: StrapiSamplingScheduleRow): boolean {
  if (String(schedule.admin_sampling_completed_at ?? '').trim()) return true;
  if (String(schedule.approved_at ?? '').trim()) return true;
  return schedule.progress_status === 'approved';
}

function formatCalendarDate(value: string): string {
  const day = value.trim().slice(0, 10);
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!match) return value.trim();
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' });
}

function approvedScheduleDatesLabel(schedule: StrapiSamplingScheduleRow): string | null {
  const dates = [...new Set((schedule.customer_agreed_dates ?? []).map((date) => date.trim().slice(0, 10)))]
    .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    .sort();
  if (dates.length) return dates.map(formatCalendarDate).join(', ');
  const start = String(schedule.customer_agreed_start_date ?? '').trim();
  const end = String(schedule.customer_agreed_end_date ?? '').trim();
  if (start && end) return `${formatCalendarDate(start)} – ${formatCalendarDate(end)}`;
  if (start) return formatCalendarDate(start);
  return null;
}

function workOrderLabel(schedule: StrapiSamplingScheduleRow): string | null {
  const jobNumber = String(schedule.work_order?.job_number ?? '').trim();
  const documentId = String(schedule.work_order?.documentId ?? '').trim();
  if (jobNumber && documentId) return `${jobNumber} (${documentId})`;
  return jobNumber || documentId || null;
}

function jenisPengukuranLabelForGroup(
  samples: StrapiSampleInformationRow[] | undefined,
  groupId: string,
): string {
  return (samples ?? [])
    .filter((line) => line.quotation_group?.documentId?.trim() === groupId)
    .map((line) => {
      const name = String(line.jenis_pengukuran?.nama_jenis ?? '').trim();
      return name || formatJenisPengukuranLine(line.jenis_pengukuran);
    })
    .filter(Boolean)
    .join(', ');
}

function contractGroupTitle(
  group: StrapiQuotationGroupRow,
  groupIndex: number,
  quotation: Pick<
    StrapiQuotationRow,
    | 'is_contract'
    | 'is_consultant_quote'
    | 'contract_period_start_date'
    | 'contract_period_end_date'
  >,
): string {
  return (
    buildGroupLabel(
      group as unknown as Record<string, unknown>,
      groupIndex,
      {
        is_contract: quotation.is_contract,
        is_consultant_quote: quotation.is_consultant_quote,
        contract_period_start_date: quotation.contract_period_start_date ?? null,
        contract_period_end_date: quotation.contract_period_end_date ?? null,
      },
    ) ?? `Group ${groupIndex + 1}`
  );
}

export function ContractScheduleEventDialog({
  open,
  jwt,
  quotationDocumentId,
  onClose,
  onEnsured,
}: Props) {
  const navigate = useNavigate();
  const onEnsuredRef = useRef(onEnsured);
  onEnsuredRef.current = onEnsured;
  const [rows, setRows] = useState<ContractScheduleRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    if (!jwt || !quotationDocumentId) {
      setRows([]);
      setError('Sesi tidak ditemukan.');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setProgress(null);
    setError(null);
    setRows([]);
    void (async () => {
      try {
        const detail = await strapiGetQuotation(jwt, quotationDocumentId);
        const groups = (detail.quotation_groups ?? []).filter((group) => group.documentId?.trim());
        const slots = groups
          .flatMap((group, groupIndex) =>
            (group.quotation_recurring_events ?? [])
              .filter((event) => event.documentId?.trim())
              .map((event) => ({ group, groupIndex, event })),
          )
          .sort((a, b) => {
            const dateA = a.event.scheduled_date.trim().slice(0, 10);
            const dateB = b.event.scheduled_date.trim().slice(0, 10);
            if (dateA !== dateB) return dateA < dateB ? -1 : 1;
            if (a.event.event_index !== b.event.event_index) return a.event.event_index - b.event.event_index;
            return a.groupIndex - b.groupIndex;
          });
        if (!slots.length) {
          if (!cancelled) {
            setError('Quotation kontrak ini belum punya index recurring yang tersimpan.');
          }
          return;
        }
        const jenisByGroupId = new Map(
          groups.map((group) => [
            group.documentId.trim(),
            jenisPengukuranLabelForGroup(detail.sample_informations, group.documentId.trim()),
          ]),
        );
        const next: ContractScheduleRow[] = [];
        for (const [index, slot] of slots.entries()) {
          if (cancelled) return;
          setProgress(`Menyiapkan jadwal ${index + 1} dari ${slots.length}…`);
          const groupId = slot.group.documentId.trim();
          const eventId = slot.event.documentId.trim();
          const schedule = await strapiEnsureSamplingScheduleForQuotation(
            jwt,
            quotationDocumentId,
            groupId,
            eventId,
          );
          const scheduleId = String(schedule.documentId ?? '').trim();
          if (!scheduleId) {
            throw new Error(
              `Gagal membuat penjadwalan untuk ${contractGroupTitle(slot.group, slot.groupIndex, detail)}, jadwal ke-${slot.event.event_index}.`,
            );
          }
          const approvedByAdminSampling = scheduleApprovedByAdminSampling(schedule);
          next.push({
            eventId,
            title: `${contractGroupTitle(slot.group, slot.groupIndex, detail)} · Jadwal ke-${slot.event.event_index}`,
            jenisPengukuranLabel: jenisByGroupId.get(groupId) ?? '',
            recurringDateLabel: formatCalendarDate(slot.event.scheduled_date),
            scheduleId,
            workOrderId: workOrderLabel(schedule),
            approvedDatesLabel: approvedByAdminSampling ? approvedScheduleDatesLabel(schedule) : null,
            approvedByAdminSampling,
          });
        }
        if (cancelled) return;
        setRows(next);
        onEnsuredRef.current?.();
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Gagal menyiapkan penjadwalan kontrak.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setProgress(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, jwt, quotationDocumentId]);

  function openSchedule(scheduleId: string) {
    onClose();
    navigate(`/sampling/schedules/${encodeURIComponent(scheduleId)}`);
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Penjadwalan sampling</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Setiap group punya satu jadwal untuk setiap index recurring yang tersimpan. Pilih baris
          untuk membuka penjadwalannya.
        </Typography>
        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">{progress ?? 'Menyiapkan jadwal kontrak…'}</Typography>
          </Stack>
        ) : null}
        {error ? <Alert severity="error">{error}</Alert> : null}
        {!loading && rows.length > 0 ? (
          <List disablePadding>
            {rows.map((row) => (
              <ListItemButton
                key={row.eventId}
                onClick={() => openSchedule(row.scheduleId)}
                data-testid={`contract-schedule-event-${row.eventId}`}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  alignItems: 'flex-start',
                }}
              >
                <Stack spacing={0.5} sx={{ width: '100%', py: 0.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle2">{row.title}</Typography>
                    <Chip
                      size="small"
                      color={row.approvedByAdminSampling ? 'success' : 'warning'}
                      label={
                        row.approvedByAdminSampling
                          ? 'Sudah disetujui admin sampling'
                          : 'Belum disetujui admin sampling'
                      }
                    />
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Jenis pengukuran: {row.jenisPengukuranLabel || '—'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Tanggal recurring: {row.recurringDateLabel}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Jadwal: {row.scheduleId}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Work order: {row.workOrderId ?? 'Belum ada'}
                  </Typography>
                  {row.approvedByAdminSampling ? (
                    <Typography variant="body2" color="text.secondary">
                      Tanggal disetujui: {row.approvedDatesLabel ?? 'Belum ada tanggal'}
                    </Typography>
                  ) : null}
                </Stack>
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Tutup
        </Button>
      </DialogActions>
    </Dialog>
  );
}
